import {
  ApplicationRef,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  Signal,
  signal,
  WritableSignal,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';
import { NgxMaskDirective, NgxMaskService } from 'ngx-mask';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { GetCountriesService } from '../../services/get-countries.service';
import { ShopSettingsService } from './services/shop-settings.service';
import { MatDialog } from '@angular/material/dialog';
import { catchError, EMPTY, filter, map, tap } from 'rxjs';
import { ShopData } from './interfaces/shop-request.interface';
import { ShopDataResponse } from './interfaces/shop-response.interface';
import { shippingAddressValidator } from '../../user-profile/user-profile-general/custom-validator/shipping-address.validator';
import { cityValidator } from '../../user-profile/user-profile-general/custom-validator/city.validator';
import { stateValidator } from '../../user-profile/user-profile-general/custom-validator/state.validator';
import { Country, CountriesList } from '@app/utils/interfaces/countries-list';
import { UpdateShop } from '@app/utils/interfaces/update-shop.interface';
import { leadingOrTrailingWhitespaceValidator } from '@app/utils/validators/leading-or-trailing-whitespace.validator';
import { ConfirmModalComponent } from '@app/components/shared/confirm-modal/confirm-modal.component';
import { PhoneNumberFormFieldComponent } from '@app/components/shared/phone-number-form-field/phone-number-form-field.component';
import { ShopSettingsGeneralForm } from '@app/utils/interfaces/shop-settings-general-form.interface';
import { emailValidator } from '@app/pages/login-page/validators/email.validator';
import { UserStateService } from '@app/services/user-state/user-state.service';
import { urlSlugCustomFormatValidator } from '@app/utils/validators/url-custom-format';
import {
  SETTINGS_GENERAL_FORM_FIELD_ERROR_MESSAGE_KEYS_MAP,
  SETTINGS_GENERAL_PHONE_FORM_FIELD_ERROR_MESSAGE_KEYS_MAP,
} from '@app/utils/constants/settings-general-form-field-errors';
import { createMaskFromRegexString } from '@app/utils/helpers/create-mask-from-regex/create-mask-from-regex-string';
import { MaskOptions, Mask } from '@app/utils/interfaces/mask.interface';
import { descriptionValidator } from '@app/utils/validators/description-validator';
import { SnackbarStatus } from '@app/utils/enums/snackbar.enum';
import { SnackbarService } from '@app/services/snackbar/snackbar.service';
import { REGEX } from '@app/utils/constants/regex.constants';

@Component({
  selector: 'app-settings-general',
  standalone: true,
  imports: [
    TranslateModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    ReactiveFormsModule,
    NgxMaskDirective,
    PhoneNumberFormFieldComponent,
  ],
  templateUrl: './settings-general.component.html',
  styleUrl: './settings-general.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SettingsGeneralComponent {
  protected readonly snackbarService = inject(SnackbarService);

  protected form: FormGroup<ShopSettingsGeneralForm>;
  protected countries: Signal<Country[]> = signal<Country[]>([]);
  protected shopData = signal<ShopData | null>(null);
  protected dataSaved = signal(false);
  protected selectedCountry = signal<Country | null>(null);
  protected phoneMaskOptions = signal<MaskOptions>({ mask: '', isValidationOn: true });
  protected zipMaskOptions = signal<MaskOptions>({ mask: '', isValidationOn: true });

  protected readonly ERROR_MESSAGE_KEYS_MAP = SETTINGS_GENERAL_FORM_FIELD_ERROR_MESSAGE_KEYS_MAP;

  protected readonly PHONE_ERRORS_KEYS_MAP =
    SETTINGS_GENERAL_PHONE_FORM_FIELD_ERROR_MESSAGE_KEYS_MAP;

  private readonly countriesService = inject(GetCountriesService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly shopSettingsService = inject(ShopSettingsService);
  private readonly dialog = inject(MatDialog);
  private readonly fb = inject(FormBuilder);
  private readonly userStateService = inject(UserStateService);
  private readonly ngxMaskService = inject(NgxMaskService);
  private readonly applicationRef = inject(ApplicationRef);

  private readonly shopId = computed(() => {
    return this.userStateService.userData()?.data.shopId;
  });

  constructor() {
    this.form = this.fb.group({
      name: this.fb.nonNullable.control('', [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(90),
        leadingOrTrailingWhitespaceValidator(),
        Validators.pattern(REGEX.shopName),
      ]),
      email: this.fb.nonNullable.control('', [
        Validators.required,
        Validators.email,
        emailValidator(),
      ]),
      phoneNumber: this.fb.nonNullable.control('', Validators.required),
      url: this.fb.nonNullable.control('', [Validators.required, urlSlugCustomFormatValidator()]),
      address: this.fb.nonNullable.control('', [shippingAddressValidator()]),
      countryId: this.fb.nonNullable.control(''),
      city: this.fb.nonNullable.control('', [cityValidator()]),
      state: this.fb.nonNullable.control('', [stateValidator()]),
      zipCode: this.fb.nonNullable.control(''),
      description: this.fb.nonNullable.control('', [descriptionValidator()]),
    });

    this.countries = toSignal(
      this.countriesService.getCountries().pipe(map(({ data }: CountriesList) => data)),
      { initialValue: [] },
    );

    effect(() => {
      const shopId = this.shopId();

      if (shopId && this.countries().length > 0) {
        this.shopSettingsService
          .getShop(shopId.toString())
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: ({ data: shopData }: ShopDataResponse) => {
              this.shopData.set(shopData);
              this.selectedCountry.set(
                this.countries().find((c: Country) => c.id === shopData.countryId)!,
              );
              this.setUserData(this.shopData()!);
              this.updatePlaceholders(this.selectedCountry()!);
            },
          });
      }
    });
  }

  public tryGetFirstError<K extends keyof ShopSettingsGeneralForm>(controlName: K): string {
    const errors = this.form.get(controlName)?.errors;
    const hasError = this.ERROR_MESSAGE_KEYS_MAP.has(controlName);

    if (errors && hasError) {
      const firstErrorKey = Object.keys(errors)[0];

      return this.ERROR_MESSAGE_KEYS_MAP.get(controlName)![firstErrorKey];
    }

    return '';
  }

  public onSubmit(): void {
    if (this.form.invalid || this.form.pristine) {
      return;
    }

    this.shopSettingsService
      .updateShop(this.getPayload())
      .pipe(
        tap(() => {
          this.snackbarService.openSnackbar(SnackbarStatus.SUCCESS);
        }),
        catchError(() => {
          this.snackbarService.openSnackbar(SnackbarStatus.FAIL);
          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  public cancelDataChange(): void {
    if (!this.form.dirty) return;

    this.dialog
      .open(ConfirmModalComponent, {
        panelClass: 'no-radius-dialog',
        width: '42vw',
        maxWidth: '2160px',
      })
      .afterClosed()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((value: boolean) => value),
      )
      .subscribe(() => {
        this.setUserData(this.shopData()!);
        this.selectedCountry.set(
          this.countries().find((c: Country) => c.id === this.shopData()?.countryId)!,
        );
        this.form.markAsUntouched();
      });
  }

  protected getFormControl<K extends keyof ShopSettingsGeneralForm>(
    key: K,
  ): ShopSettingsGeneralForm[K] {
    return this.form.controls[key];
  }

  protected countrySelectionChange(matEvent: MatSelectChange): void {
    const country = this.countries().find((c: Country) => c.name === matEvent.value);

    if (!country) {
      return;
    }

    this.updatePlaceholders(country);
  }

  protected updatePlaceholders(selectedCountry: Country): void {
    this.selectedCountry.set(selectedCountry);

    this.updateControlMask(
      this.selectedCountry()!.phoneMask,
      this.phoneMaskOptions,
      this.getFormControl('phoneNumber'),
    );

    this.updateControlMask(
      this.selectedCountry()!.zipCodeMask,
      this.zipMaskOptions,
      this.getFormControl('zipCode'),
    );
  }

  protected transformZipTextToUppercase(value: unknown): string {
    return typeof value === 'string' ? value.toUpperCase() : '';
  }

  private updateControlMask(
    maskRegexString: string,
    maskOptionsSignal: WritableSignal<MaskOptions>,
    control: FormControl<string>,
  ): void {
    const newMask: Mask = createMaskFromRegexString(maskRegexString);

    control.setValue(this.ngxMaskService.applyMask(control.value, newMask.mask));

    maskOptionsSignal.update((prevOptions: MaskOptions) => {
      return {
        ...prevOptions,
        ...newMask,
      };
    });

    // NgxMaskDirective validates only after directive is applied.
    this.applicationRef.whenStable().then(() => {
      control.updateValueAndValidity();
      control.markAllAsTouched();
    });
  }

  private setUserData(data: UpdateShop): void {
    this.form.patchValue({
      ...data,
      countryId: this.selectedCountry()!.name,
    });
  }

  private getPayload(): UpdateShop {
    const formttedPhoneNumber = this.ngxMaskService.applyMask(
      this.getFormControl('phoneNumber').value,
      this.phoneMaskOptions().mask,
    );

    const formattedZipCode =
      (this.zipMaskOptions().prefix ?? '') +
      this.ngxMaskService.applyMask(
        this.getFormControl('zipCode').value,
        this.zipMaskOptions().mask,
      );

    return {
      name: this.getFormControl('name').value,
      email: this.getFormControl('email').value,
      phoneNumber: formttedPhoneNumber,
      url: this.getFormControl('url').value,
      address: this.getFormControl('address').value,
      countryId: this.selectedCountry()!.id,
      city: this.getFormControl('city').value,
      state: this.getFormControl('state').value,
      zipCode: formattedZipCode,
      description: this.getFormControl('description').value,
    };
  }
}
